import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { API_CONFIG } from '../config/apiConfig';

const MiniCart = ({ isOpen, onClose }) => {
  const cart = useCart();
  const navigate = useNavigate();
  const [isAgreed, setIsAgreed] = useState(false);

  const updateQuantity = (id, change, variant = null) => {
    const item = cart.cart.find(item => item.id === id && (variant === null ? true : (item.variant !== undefined ? item.variant : 0) === (variant !== undefined ? variant : 0)));
    if (item) {
      const newQuantity = item.quantiy + change;
      if (newQuantity <= 0) {
        cart.updateQuantity(id, 0, item.variant); // This will remove the item
      } else {
        cart.updateQuantity(id, newQuantity, item.variant);
      }
    }
  };

  const removeItem = (id, variant = null) => {
    cart.removeItem(id, variant);
  };

  const calculateTotal = () => {
    return cart.totalPrice.toFixed(2);
  };

  const getItemImage = (item) => {
    if (item.cover && typeof item.cover === 'string' && item.cover.trim()) {
      return item.cover.startsWith('http') ? item.cover : `${API_CONFIG.IMAGE_URL}${item.cover}`;
    }
    return '/assets/img/product/default.png';
  };

  const parsePrice = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const str = String(value).trim();
    if (str === '') return 0;
    const cleaned = str.replace(/[^0-9.-]+/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const getItemPrice = (item) => {
    // For variation products with discount field, use discount as the final price
    if (item.variant !== null && item.variant !== undefined && item.variations && item.variations[0]?.items?.[item.variant]) {
      const variantData = item.variations[0].items[item.variant];
      const variantDiscount = parseFloat(variantData.discount) || 0;
      
      if (variantDiscount > 0) {
        // Use discount field as the final price
        return variantDiscount;
      }
      // No discount field, use variant price
      return parsePrice(variantData.price);
    }
    
    // For non-variation products, use sell_price or original_price
    const discountValue = parseFloat(item.discount) || 0;
    if (discountValue === 0) {
      return parsePrice(item.original_price);
    }
    return parsePrice(item.sell_price);
  };

  return (
    <div className={`offCanvas__minicart ${isOpen ? 'active' : ''}`}>
      <div className="minicart__header">
        <div className="minicart__header--top d-flex justify-content-between align-items-center">
          <h3 className="minicart__title">Shopping Cart</h3>
          <button className="minicart__close--btn" aria-label="minicart close btn" onClick={onClose}>
            <svg className="minicart__close--icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path fill="currentColor" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="M368 368L144 144M368 144L144 368"/>
            </svg>
          </button>
        </div>
        <p className="minicart__header--desc">The organic foods products are limited</p>
      </div>

      <div className="minicart__product">
        {cart.cart.length === 0 ? (
          <div className="text-center py-4">
            <p>Your cart is empty</p>
          </div>
        ) : (
          cart.cart.map(item => (
            <div key={item.id} className="minicart__product--items d-flex">
              <div className="minicart__thumb">
                <Link to={`/product/${item.id}`}>
                  <img src={getItemImage(item)} alt={item.name} />
                </Link>
              </div>
              <div className="minicart__text">
                <h4 className="minicart__subtitle">
                  <Link to={`/product/${item.id}`}>
                    {item.name}
                    {item.variant !== null && item.variant !== undefined && item.variations && item.variations[0]?.items?.[item.variant] 
                      ? ` (${item.variations[0].items[item.variant].name})` 
                      : ''}
                  </Link>
                </h4>
                <div className="minicart__price">
                  <span className="current__price">${getItemPrice(item).toFixed(2)}</span>
                  {parseFloat(item.discount) > 0 && (
                    <span className="old__price">${parseFloat(item.original_price || item.price || '0').toFixed(2)}</span>
                  )}
                </div>
                <div className="minicart__text--footer d-flex align-items-center">
                  <div className="quantity__box minicart__quantity">
                    <button
                      type="button"
                      className="quantity__value decrease"
                      aria-label="quantity value"
                      onClick={() => updateQuantity(item.id, -1, item.variant)}
                    >
                      -
                    </button>
                    <label>
                      <input type="number" className="quantity__number" value={item.quantiy} readOnly />
                    </label>
                    <button
                      type="button"
                      className="quantity__value increase"
                      aria-label="quantity value"
                      onClick={() => updateQuantity(item.id, 1, item.variant)}
                    >
                      +
                    </button>
                  </div>
                  <button
                    className="minicart__product--remove"
                    type="button"
                    onClick={() => removeItem(item.id, item.variant)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {cart.cart.length > 0 && (
        <>
          <div className="minicart__amount">
            <div className="minicart__amount_list d-flex justify-content-between">
              <span>Sub Total:</span>
              <span><b>${cart.totalPrice.toFixed(2)}</b></span>
            </div>
            {cart.discount > 0 && (
              <div className="minicart__amount_list d-flex justify-content-between">
                <span>Discount:</span>
                <span><b>-${cart.discount.toFixed(2)}</b></span>
              </div>
            )}
            {cart.deliveryPrice > 0 && (
              <div className="minicart__amount_list d-flex justify-content-between">
                <span>Delivery:</span>
                <span><b>${cart.deliveryPrice.toFixed(2)}</b></span>
              </div>
            )}
            {cart.orderTax > 0 && (
              <div className="minicart__amount_list d-flex justify-content-between">
                <span>Tax:</span>
                <span><b>${((cart.totalPrice * cart.orderTax) / 100).toFixed(2)}</b></span>
              </div>
            )}
            <div className="minicart__amount_list d-flex justify-content-between">
              <span>Total:</span>
              <span><b>${cart.grandTotal.toFixed(2)}</b></span>
            </div>
          </div>

          <div className="minicart__conditions d-flex align-items-center" style={{gap: '8px'}}>
            <input 
              className="minicart__conditions--input" 
              id="accept" 
              type="checkbox" 
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
            />
            <label className="minicart__conditions--label" htmlFor="accept">
              I agree with the <Link className="minicart__conditions--link" to="/privacy-policy">Privacy Policy</Link>
            </label>
          </div>

          <div className="minicart__button d-flex justify-content-center">
            <Link className="btn minicart__button--link" to="/cart">View cart</Link>
            <button 
              className="btn minicart__button--link" 
              onClick={(e) => {
                e.preventDefault();
                if (!isAgreed) {
                  alert('Please agree to the Privacy Policy before proceeding to checkout.');
                  return;
                }
                navigate('/checkout');
              }}
              style={{
                border: 'none',
                cursor: isAgreed ? 'pointer' : 'not-allowed',
                opacity: isAgreed ? 1 : 0.6
              }}
            >
              Checkout
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default MiniCart;
